import React, { useEffect, useState } from "react";
import { X, Wallet } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import "./fixedAssets.css";

export default function PostAcquisitionModal({
  companyId,
  asset,
  coa,
  onClose,
  onPosted,
}) {
  const [creditAccountId, setCreditAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // pick a reasonable default: first Asset category "Bank"/"Cash" if exists; otherwise blank
    const bankish = (coa || []).find((a) =>
      String(a.category || "")
        .toLowerCase()
        .includes("bank")
    );
    if (bankish?.id) setCreditAccountId(bankish.id);
  }, [coa]);

  async function callFn(fnName, payload) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error("Not authenticated.");

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

    if (!supabaseUrl) {
      throw new Error("Missing REACT_APP_SUPABASE_URL.");
    }

    const fnUrl = `${supabaseUrl}/functions/v1/${fnName}`;

    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json?.error || "Request failed.");
    return json;
  }

  async function post() {
    setErr("");
    setBusy(true);
    try {
      if (!companyId) throw new Error("Missing companyId.");
      if (!asset?.id) throw new Error("Missing asset.");
      if (!creditAccountId)
        throw new Error("Select a credit (paid-from/payable) account.");

      const ok = window.confirm(
        `Post acquisition journal?\n\nDr Fixed Asset (cost)\nCr Selected account\n\nAsset: ${asset.asset_tag} ${asset.name}`
      );
      if (!ok) return;

      const json = await callFn("fixed-assets-post-acquisition", {
        company_id: companyId,
        asset_id: asset.id,
        credit_account_id: creditAccountId,
      });

      onPosted?.(json);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.97)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(820px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Post Acquisition Journal
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Asset: <b>{asset?.asset_tag}</b> — {asset?.name}
            </div>
          </div>
          <button className="fa-icon-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="fa-card" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            This creates ledger entries (journal-ledger architecture):
            <div style={{ marginTop: 6, fontWeight: 900 }}>
              Dr Fixed Asset • Cr Paid-from / Payable account
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
            Paid from / Payable account (Credit)
          </div>
          <select
            className="fa-select"
            value={creditAccountId}
            onChange={(e) => setCreditAccountId(e.target.value)}
            disabled={busy}
          >
            <option value="">Select account…</option>
            {(coa || []).map((a) => (
              <option key={a.id} value={a.id}>
                {(a.code ? `${a.code} - ` : "") + a.name} ({a.type}/
                {a.category || "—"})
              </option>
            ))}
          </select>
        </div>

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}

        <div
          className="fa-actions"
          style={{ marginTop: 14, justifyContent: "flex-end" }}
        >
          <button className="fa-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={post}
            disabled={busy}
          >
            <Wallet size={16} /> {busy ? "Posting…" : "Post Journal"}
          </button>
        </div>
      </div>
    </div>
  );
}
